import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import {
  ArrowDownTrayIcon,
  FolderPlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import {
  ChangeEvent,
  useMemo,
  useRef,
  useState,
} from 'react';
import { When } from 'react-if';
import { toast } from 'react-toastify';

import { Card } from "../../../../common/Atoms/Card";
import { MultiButtonCardHeader } from "../../../../common/Atoms/MultiButtonCardHeader";
import { CardContent } from '../../../../common/Atoms/CardContent';
import { useGetSelectedOrganisation } from '../../../hooks/useGetSelectedOrganisation';
import { useFiles } from '../../../hooks/useFiles';
import { useDeleteFileMutation, useUploadFileMutation } from '../../../services/api/filesApi/files';
import { Paragraph } from '../../../../common/Atoms/Typography/Paragraph';
import { SmallSpinner } from '../../../../common/Atoms/SmallSpinner';
import { useApiRequest } from '../../../hooks/useApiRequest';
import { env } from '../../../../common/lib/env';

export function FilesRoot() {
  const currentOrg = useGetSelectedOrganisation();
  const fileUploderRef = useRef(null);
  const { data: filesResponse } = useFiles();
  const [ uploadFile, { isLoading: isUploading } ] = useUploadFileMutation();
  const [ deleteFile ] = useDeleteFileMutation();
  const request = useApiRequest();
  const [ deleting, setDeleting ] = useState<number | null>(null);
  const [ downloading, setDownloading ] = useState<number | null>(null);

  function onUploadClick() {
    // Programmatically click the file uploader
    if (fileUploderRef.current?.click) {
      fileUploderRef?.current?.click();
    }
  }

  async function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const fileUploaded = event.target.files[0];

    const formData = new FormData();

    formData.append(`file`, fileUploaded);

    const response = await uploadFile({
      org: currentOrg?.id,
      formData,
    });

    console.log(response);

    fileUploderRef.current.value = null;
  }

  const filesTree = useMemo(() => {
    if (!filesResponse || !filesResponse.files) return [];

    return filesResponse.files.map(f => ({
      ...f,
      name: f.originalFileName,
      id: f.id,
    }));
  }, [filesResponse]);

  async function onDelete(id: number) {
    setDeleting(id);

    await deleteFile({
      org: currentOrg?.id,
      id,
    });

    setDeleting(null);
  }

  async function onDownload(id: number, fileName: string) {
    if (downloading === id) return;

    setDownloading(id);
    try {
      const result = await request({
        method: `GET`,
        responseType: `blob`,
        url: `${env.baseApiUrl}files/${id}/download`,
      });

      const blobUrl = window.URL.createObjectURL(new Blob([result.data]));
      const link = document.createElement(`a`);
      link.href = blobUrl;
      link.setAttribute(`download`, fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    }
    catch (e) {
      const responseObj = await e.response?.data?.text?.();
      toast.error(`${responseObj ? `${responseObj}` : `Could not download file.`}`);
    }
    finally {
      setDownloading(null);
    }
  }

  return (
    <Card>
      <input
        type={ `file` }
        ref={ fileUploderRef }
        onChange={ handleChange }
        className={ `hidden` }
      />
      <MultiButtonCardHeader
        title={ `Files` }
        description= { `Manage your file uploads, for use in email attachments. E.g Your terms and conditions.` }
        secondaryDescription={ `Max file size: 10MB` }
        buttons={ [
          {
            buttonText: `Upload File`,
            onClick: onUploadClick,
            icon: FolderPlusIcon,
            loading: isUploading,
            disabled: isUploading,
          },
        ] }
      />

      <CardContent className={ `mt-4` }>
        <When condition={ isUploading }>
          <Paragraph>
            { `Uploading file...` }
          </Paragraph>
        </When>
        <SimpleTreeView
          expandedItems={ [`root`] }
        >
          <TreeItem
            itemId={ `root` }
            label={ `${currentOrg?.legalName}'s Files` }
          >
            { filesTree.map(file => {
              const labelNode = (
                <div className={ `grid grid-cols-12` }>
                  <Paragraph className={ `col-span-6` }>
                    { file.name }
                  </Paragraph>

                  <Paragraph className={ `col-span-2` }>
                    { file.sizeFormatted }
                  </Paragraph>

                  <Paragraph className={ `col-span-2` }>
                    { new Date(file.uploadedAt).toLocaleString() }
                  </Paragraph>

                  <span className={ `col-span-2 flex space-x-2` }>
                    <button
                      onClick={ e => {
                        e.stopPropagation();
                        onDownload(file.id, file.originalFileName);
                      } }
                      disabled={ downloading === file.id || deleting === file.id }
                    >
                      <When condition={ downloading === file.id }>
                        <SmallSpinner colored />
                      </When>
                      <When condition={ downloading !== file.id }>
                        <ArrowDownTrayIcon className={ `h-6 w-6 text-gray-500` } />
                      </When>
                    </button>

                    <button
                      onClick={ e => {
                        e.stopPropagation();
                        onDelete(file.id);
                      } }
                      disabled={ deleting === file.id || downloading === file.id }
                    >
                      <When condition={ deleting === file.id }>
                        <SmallSpinner colored />
                      </When>
                      <When condition={ deleting !== file.id }>
                        <TrashIcon className={ `h-6 w-6 text-gray-500` } />
                      </When>
                    </button>
                  </span>
                </div>
              );

              return (
                <TreeItem
                  key={ file.id }
                  itemId={ file.id.toString() }
                  label={ labelNode }
                />
              );
            }) }
          </TreeItem>
        </SimpleTreeView>
      </CardContent>
    </Card>
  );
}
